import {
  useLatest,
  useLocalStorageState,
  useMemoizedFn,
  useSessionStorageState,
} from 'ahooks'
import { logger } from '@loc/helpers/src/logger/client'

import { getConnectParams } from '../helpers/connectParams'

import { useSessionInfo } from './sessionInfo'

import { useAnalyticsData } from '@/hooks/analyticsData'
import { getShortRequestId } from '@/helpers/getRequestId'

const SignInTrackType = 'sign-in-track-type'
const LockName = 'sign-in-track-lock'

/**
 * 纯函数：清理所有登录追踪状态（localStorage）。
 * 独立于 hook 体系，可安全在 login.ts / signUp.ts 的 finally 中调用，
 * 不会引入 useAnalyticsData 的循环依赖。
 */
export function removeTrackAuth() {
  try {
    localStorage.removeItem(SignInTrackType)
    localStorage.removeItem('sign-in-track-origin-url')
    // 释放锁：只有当前 tab 持有锁时才清除
    const currentLock = localStorage.getItem(LockName)
    const tabId = sessionStorage.getItem('sign-in-track-tab-id')
    if (currentLock && tabId) {
      try {
        const parsed = JSON.parse(currentLock) as string | undefined
        if (parsed && parsed.split(':')[1] === JSON.parse(tabId)) {
          localStorage.removeItem(LockName)
        }
      } catch {
        // JSON parse 失败，直接清除
        localStorage.removeItem(LockName)
      }
    }
  } catch (error) {
    logger.dev.error('removeTrackAuth error:', error)
  }
}

export function useTrackSignInOrSignUp({
  enabled = false,
}: {
  enabled?: boolean
}) {
  const { userId, isSignIn, trigger, email, userDateCreated } = useSessionInfo()
  const { trackCustomEvent } = useAnalyticsData()
  const url = useUrl()

  // 是否已经执行过追踪
  const hasTracked = useRef<boolean>(false)

  const [tabId, setTabId] = useSessionStorageState<undefined | string>(
    'sign-in-track-tab-id',
    {
      defaultValue: undefined,
      listenStorageChange: true,
    },
  )

  const tabIdLatest = useLatest(tabId)

  const [authMethod, setAuthMethod] = useLocalStorageState<
    'email' | 'Google' | '' | undefined
  >(SignInTrackType, { defaultValue: '', listenStorageChange: true })

  const [originUrlState, setOriginUrlState] = useLocalStorageState<
    string | undefined
  >('sign-in-track-origin-url', { defaultValue: '', listenStorageChange: true })

  const [lock, setLock] = useLocalStorageState<string | undefined>(LockName, {
    defaultValue: '',
    listenStorageChange: true,
  })

  const lockLatest = useLatest(lock)

  // 尝试获取跟踪锁
  const acquireTrackingLock = useMemoizedFn(() => {
    // 生成随机延迟以减少冲突概率
    const randomDelay = Math.floor(Math.random() * 300)

    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        try {
          const currentLock = lockLatest.current

          if (currentLock) {
            const [timestamp, lockTabId] = currentLock.split(':')
            // 如果锁已存在且未过期（5秒），且不是当前标签页的锁
            if (
              Date.now() - Number(timestamp) < 5000 &&
              lockTabId !== tabIdLatest.current
            ) {
              resolve(false)
              return
            }
          }

          // 设置新锁（时间戳:标签页ID）
          const newLock = `${Date.now()}:${tabIdLatest.current}`
          setLock(newLock)

          // 添加一个微小延迟进行最终检查（防止竞态条件）
          setTimeout(() => {
            const finalCheck = lockLatest.current === newLock
            resolve(finalCheck)
          }, 100)
        } catch (e) {
          resolve(false)
        }
      }, randomDelay)
    })
  })

  // 释放跟踪锁（仅当是当前标签页设置的锁）
  const releaseTrackingLock = useMemoizedFn(() => {
    try {
      const currentLock = lockLatest.current
      if (currentLock && currentLock.split(':')[1] === tabIdLatest.current) {
        setLock(undefined)
      }
    } catch (error) {
      logger.dev.error(error)
    }
  })

  // 监听登录状态变化，在登出时重置标志
  useEffect(() => {
    if (!userId && hasTracked.current) {
      hasTracked.current = false
    }
  }, [userId])

  // 登录成功后上报日志
  useEffect(() => {
    // 如果已经追踪过或不满足条件，则不执行
    if (
      hasTracked.current ||
      !enabled ||
      url.pathname === '/sign-in-google' ||
      url.pathname === '/log-in-google' ||
      !authMethod ||
      !userId ||
      !isSignIn ||
      !email
    ) {
      return
    }

    if (!tabId) {
      setTabId(getShortRequestId())
      return
    }

    // 尝试获取锁，只有获得锁的标签页才执行跟踪
    let isLocked = false

    const attemptTracking = async () => {
      isLocked = await acquireTrackingLock()

      if (!isLocked) {
        return
      }

      // 标记为已追踪
      hasTracked.current = true

      // 获取来源 URL
      const rawOriginUrl = originUrlState || ''
      let parsedOriginUrl = ''

      try {
        parsedOriginUrl =
          new URL(rawOriginUrl).searchParams.get('originUrl') || ''
      } catch (error) {
        parsedOriginUrl = ''
      }

      const connectParams = getConnectParams()
      const origin_referrer = connectParams.redirect_uri
        ? process.env.NEXT_PUBLIC_BYPASSGPT_CHAT_OPEN_AI_URL || '/'
        : undefined

      const meta = {
        origin_url: parsedOriginUrl || rawOriginUrl || '',
        ...(origin_referrer ? { origin_referrer } : {}),
      }

      // 根据登录方式和触发类型发送对应事件
      if (authMethod === 'Google' && trigger === 'signUp') {
        trackCustomEvent({
          name: 'sign_up',
          method: authMethod,
          status: 0,
          meta,
          latestUserId: userId,
          latestEmail: email.toLowerCase(),
        })
      } else {
        trackCustomEvent({
          name: 'login',
          method: authMethod,
          status: 0,
          meta,
          latestUserId: userId,
          latestEmail: email.toLowerCase(),
        })
      }
    }

    attemptTracking()

    // 确保组件卸载时释放锁
    return () => {
      if (isLocked) {
        releaseTrackingLock()
      }
    }
  }, [
    enabled,
    userId,
    isSignIn,
    email,
    trigger,
    userDateCreated,
    authMethod,
    originUrlState,
    url.pathname,
    tabId,
    setTabId,
    acquireTrackingLock,
    releaseTrackingLock,
    trackCustomEvent,
  ])

  return {
    setAuthMethod,
    setOriginUrlState,
  }
}
